<template>
  <header class="w-full">
    <!-- Main Navbar -->
    <nav class="relative z-50 bg-base-white">
      <div class="max-w-content mx-auto max-sm:px-6 max-xl:px-13">
        <div class="flex items-center h-20">
          <!-- Logo -->
          <router-link to="/" class="flex items-center min-w-[97px]">
            <img :src="layupLogo" alt="Layup Logo" class="h-10 w-auto" />
          </router-link>

          <!-- Desktop Navigation -->
          <div class="hidden lg:flex w-full">
            <div class="flex items-center space-x-8 ml-12">
              <template v-for="link in navLinks" :key="link.name">
                <div
                  v-if="link.dropdown"
                  class="relative nav-dropdown"
                  @mouseenter="openDesktopDropdown(link.name)"
                  @mouseleave="closeDesktopDropdown(link.name)"
                >
                  <button
                    class="flex items-center gap-1 text-grey-light-600 font-semibold hover:text-primary transition-colors py-2"
                  >
                    {{ link.name }}
                    <ChevronDown class="w-4 h-4" />
                  </button>
                  <div v-if="openDropdown === link.name" class="absolute left-0 top-full pt-2 z-50 w-max">
                    <div class="bg-white rounded-xl border border-grey-light-200 shadow-dropdown">
                      <div class="flex flex-col gap-2 px-5 py-6">
                        <button
                          v-for="item in link.dropdown"
                          :key="item.product"
                          class="w-full flex items-center gap-2 p-3 rounded-lg text-left hover:bg-grey-light-50 transition-colors text-grey-light-900 font-semibold cursor-pointer"
                          @click="navigateDropdown(item.product, item.to)"
                        >
                          {{ item.label }}
                          <span
                            v-if="item.isNew"
                            class="text-xs font-medium px-2.5 py-0.5 rounded-full text-success-700 bg-success-50"
                          >
                            New
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <router-link
                  v-else
                  :to="link.to"
                  class="text-grey-light-600 font-semibold hover:text-primary transition-colors"
                >
                  {{ link.name }}
                </router-link>
              </template>
            </div>
            <div class="ml-auto flex gap-2">
              <a :href="signUpUrl">
                <Button variant="outline" class="text-base">Sign Up</Button>
              </a>
              <a :href="signInUrl">
                <Button>Sign In</Button>
              </a>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <div class="lg:hidden ml-auto">
            <Button variant="ghost" @click="toggleMobileMenu">
              <Menu v-if="!isMobileMenuOpen" class="size-7" />
              <X v-if="isMobileMenuOpen" class="size-7" />
            </Button>
          </div>
        </div>
      </div>

      <Teleport to="body">
        <div
          v-if="isMobileMenuOpen"
          class="fixed inset-0 bg-black/50 z-40 lg:hidden"
          @click="isMobileMenuOpen = false"
        />
      </Teleport>

      <!-- Mobile Menu -->
      <transition @before-enter="beforeEnter" @enter="enter" @leave="leave">
        <div
          v-if="isMobileMenuOpen"
          class="absolute overflow-hidden left-0 right-0 top-full bg-base-white shadow-md py-6 z-50 lg:hidden"
        >
          <div class="flex flex-col gap-2">
            <template v-for="(link, linkIdx) in navLinks" :key="link.name">
              <div
                v-if="link.dropdown"
                class="pb-2"
                :class="{ 'border-b border-grey-light-200': linkIdx < navLinks.length - 1 }"
              >
                <button
                  class="w-full flex justify-between items-center text-grey-light-600 font-semibold px-6 py-3 hover:cursor-pointer"
                  @click="toggleMobileDropdown(link.name)"
                >
                  {{ link.name }}
                  <ChevronDown
                    class="w-5 h-5 text-grey-light-400 transition-transform duration-200"
                    :class="{ 'rotate-180': mobileActiveDropdown === link.name }"
                  />
                </button>
                <div v-if="mobileActiveDropdown === link.name" class="flex flex-col pb-2">
                  <button
                    v-for="item in link.dropdown"
                    :key="item.product"
                    class="flex items-center gap-2 px-8 py-1.5 text-base text-grey-light-600 font-semibold hover:bg-grey-light-50 transition-colors text-left"
                    @click="navigateMobileDropdown(item.product, item.to)"
                  >
                    {{ item.label }}
                    <span
                      v-if="item.isNew"
                      class="text-xs font-medium px-2 py-0.5 rounded-full text-success-750 bg-success-50"
                    >
                      New
                    </span>
                  </button>
                </div>
              </div>
              <div v-else class="pb-2" :class="{ 'border-b border-grey-light-200': linkIdx < navLinks.length - 1 }">
                <router-link
                  :to="link.to"
                  class="block text-grey-light-600 font-semibold px-6 py-3 hover:bg-grey-light-50 transition-colors"
                  @click="isMobileMenuOpen = false"
                >
                  {{ link.name }}
                </router-link>
              </div>
            </template>
          </div>
          <div class="flex flex-col gap-2 px-6 pt-4">
            <a :href="signInUrl">
              <Button variant="outline" class="w-full text-base">Sign In</Button>
            </a>
            <a :href="signUpUrl">
              <Button class="w-full">Sign Up</Button>
            </a>
          </div>
        </div>
      </transition>
    </nav>
  </header>
</template>

<script setup lang="ts">
import layupLogo from '@/assets/images/layup-logo.svg';
import { DASHBOARDS_URL, isProduction } from '@/config';
import { useThemeStore } from '@/stores/theme';
import { beforeEnter, enter, leave } from '@/utils/animations';
import { ChevronDown, Menu, X } from 'lucide-vue-next';
import type { Ref } from 'vue';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useRouter } from 'vue-router';
import { Button } from './ui/button';

interface DropdownItem {
  label: string;
  product: 'layby' | 'boost' | 'plans';
  isNew?: boolean;
  to: RouteLocationRaw;
}

interface PlainLink {
  name: string;
  to: RouteLocationRaw;
  dropdown?: never;
}

interface DropdownLink {
  name: string;
  dropdown: DropdownItem[];
  to?: never;
}

type NavItem = PlainLink | DropdownLink;

const activeTab = ref<'shoppers' | 'businesses'>('shoppers');
const indicatorPosition = ref<number>(0);
const indicatorWidth = ref<number>(0);
const isMobileMenuOpen = ref<boolean>(false);
const openDropdown = ref<string | null>(null);
const mobileActiveDropdown = ref<string | null>(null);

const dropdownCloseTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const openDesktopDropdown = (name: string): void => {
  if (dropdownCloseTimeout.value) {
    clearTimeout(dropdownCloseTimeout.value);
    dropdownCloseTimeout.value = null;
  }
  openDropdown.value = name;
};

const closeDesktopDropdown = (name: string): void => {
  dropdownCloseTimeout.value = setTimeout(() => {
    if (openDropdown.value === name) {
      openDropdown.value = null;
    }
  }, 75);
};

const shoppersButtonRef = ref<HTMLButtonElement | null>(null);
const businessesButtonRef = ref<HTMLButtonElement | null>(null);

const themeStore = useThemeStore();
const router = useRouter();

const dropdownProducts: { label: string; product: 'layby' | 'boost' | 'plans'; isNew?: boolean }[] = [
  { label: 'Digital Lay-By', product: 'layby' },
  { label: 'Boost Rewards', product: 'boost' },
  ...(!isProduction ? [{ label: 'Payment Plans', product: 'plans' as const, isNew: true }] : []),
];

const navLinks = computed((): NavItem[] => {
  const contactTo =
    themeStore.current === 'boost' ? '/' : themeStore.current === 'layby' ? '/contact-us' : '/layup-contact';

  return [
    {
      name: 'How It Works',
      dropdown: dropdownProducts.map((item) => ({
        ...item,
        to: { path: `/${item.product}`, hash: '#how-it-works' } as RouteLocationRaw,
      })),
    },
    {
      name: 'Shop',
      dropdown: dropdownProducts.map((item) => ({
        ...item,
        to: `/${item.product}/stores`,
      })),
    },
    { name: 'Help Center', to: '/help-center' },
    { name: 'Contact Us', to: contactTo },
  ];
});

const navigateDropdown = (product: 'layby' | 'boost' | 'plans', to: RouteLocationRaw): void => {
  openDropdown.value = null;
  themeStore.setProduct(product);
  router.push(to);
};

const navigateMobileDropdown = (product: 'layby' | 'boost' | 'plans', to: RouteLocationRaw): void => {
  themeStore.setProduct(product);
  router.push(to);
  isMobileMenuOpen.value = false;
  mobileActiveDropdown.value = null;
};

const toggleMobileDropdown = (name: string): void => {
  mobileActiveDropdown.value = mobileActiveDropdown.value === name ? null : name;
};

const updateIndicator = async (): Promise<void> => {
  await nextTick();

  const buttonRefs: Record<'shoppers' | 'businesses', Ref<HTMLButtonElement | null>> = {
    shoppers: shoppersButtonRef,
    businesses: businessesButtonRef,
  };

  const currentButtonRef = buttonRefs[activeTab.value];

  if (!currentButtonRef.value) return;

  if (activeTab.value === 'shoppers') {
    indicatorPosition.value = 0;
    indicatorWidth.value = currentButtonRef.value.offsetWidth;
  } else {
    const shoppersWidth = shoppersButtonRef.value?.offsetWidth || 0;
    indicatorPosition.value = shoppersWidth;
    indicatorWidth.value = currentButtonRef.value.offsetWidth;
  }
};

const toggleMobileMenu = (): void => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  mobileActiveDropdown.value = null;
};

const signInUrl = computed((): string => {
  return `${DASHBOARDS_URL}/shopper/auth/sign-in`;
});

const signUpUrl = computed((): string => {
  return `${DASHBOARDS_URL}/shopper/auth/sign-up`;
});

onMounted((): void => {
  updateIndicator();
  window.addEventListener('resize', updateIndicator);
});

onUnmounted((): void => {
  window.removeEventListener('resize', updateIndicator);
});
</script>
